import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { StorefrontOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { apiClient } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import PhoneInput from '../common/PhoneInput';
import { errorMessage } from '../../utils/errorMessage';
import AuthPageShell from './AuthPageShell';
import { createTextActionSx } from '../../theme/interactions';

interface RegisterForm {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  companyWebsite: string;
  referralCode: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [form, setForm] = useState<RegisterForm>({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    companyWebsite: '',
    referralCode: new URLSearchParams(location.search).get('ref')?.trim().toUpperCase() || '',
  });
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validateForm = (): boolean => {
    if (!form.firstName.trim()) {
      setError(`${t('auth.firstName')}: ${t('auth.fieldRequired')}`);
      return false;
    }
    if (!form.lastName.trim()) {
      setError(`${t('auth.lastName')}: ${t('auth.fieldRequired')}`);
      return false;
    }
    if (!form.username.trim()) {
      setError(`${t('auth.username')}: ${t('auth.fieldRequired')}`);
      return false;
    }
    if (form.username.length < 3) {
      setError(t('auth.usernameMinLength'));
      return false;
    }
    if (!form.password) {
      setError(`${t('auth.password')}: ${t('auth.fieldRequired')}`);
      return false;
    }
    if (form.password.length < 6) {
      setError(t('auth.passwordMinLength'));
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return false;
    }
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError(t('auth.emailInvalid'));
      return false;
    }
    if (!form.phoneNumber.trim()) {
      setError(`${t('auth.phoneNumber')}: ${t('auth.fieldRequired')}`);
      return false;
    }
    if (form.phoneNumber.length < 10) {
      setError(t('auth.phoneNumberMinLength'));
      return false;
    }
    if (!form.companyName.trim()) {
      setError(`${t('auth.companyName')}: ${t('auth.fieldRequired')}`);
      return false;
    }
    if (form.companyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.companyEmail)) {
      setError(t('auth.emailInvalid'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await apiClient.register({
        firstName: form.firstName,
        lastName: form.lastName,
        username: form.username,
        email: form.email,
        phoneNumber: form.phoneNumber,
        password: form.password,
        companyName: form.companyName,
        companyAddress: form.companyAddress || undefined,
        companyPhone: form.companyPhone || undefined,
        companyEmail: form.companyEmail || undefined,
        companyWebsite: form.companyWebsite || undefined,
        referralCode: form.referralCode || undefined,
      });

      // Registration successful, redirect to email verification page
      navigate('/verify-email', { 
        state: { 
          email: form.email,
          message: t('auth.registrationNextStep')
        } 
      });
    } catch (err: unknown) {
      setError(errorMessage(err, t('auth.registrationFailed')));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageShell title={t('auth.register')} description={t('auth.registrationDescription')} icon={<StorefrontOutlined />} wide>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2, rowGap: .25 }}>
            <Typography variant="overline" sx={{ gridColumn: '1 / -1', mt: 1, color: 'primary.main', fontWeight: 800, letterSpacing: '.08em' }}>{t('auth.personalInfo')}</Typography>
            <TextField
              margin="normal"
              required
              fullWidth
              id="firstName"
              label={t('auth.firstName')}
              name="firstName"
              autoComplete="given-name"
              autoFocus
              value={form.firstName}
              onChange={handleChange}
              disabled={isLoading}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="lastName"
              label={t('auth.lastName')}
              name="lastName"
              autoComplete="family-name"
              value={form.lastName}
              onChange={handleChange}
              disabled={isLoading}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label={t('auth.username')}
              name="username"
              autoComplete="username"
              value={form.username}
              onChange={handleChange}
              disabled={isLoading}
              helperText={t('auth.usernameMinLength')}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('auth.email')}
              name="email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={handleChange}
              disabled={isLoading}
              helperText={t('auth.registrationEmailHint')}
            />
            
            <PhoneInput
              value={form.phoneNumber}
              onChange={(value) => setForm({ ...form, phoneNumber: value })}
              label={t('auth.phoneNumber')}
              required
              disabled={isLoading}
              fullWidth
            />
            
            <Typography variant="overline" sx={{ gridColumn: '1 / -1', mt: 1.5, color: 'primary.main', fontWeight: 800, letterSpacing: '.08em' }}>{t('auth.password')}</Typography>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={form.password}
              onChange={handleChange}
              disabled={isLoading}
              helperText={t('auth.passwordMinLength')}
              InputProps={{ endAdornment: <InputAdornment position="end"><IconButton aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')} onClick={() => setShowPassword(value => !value)} edge="end">{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label={t('auth.confirmPassword')}
              type={showConfirmation ? 'text' : 'password'}
              id="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              disabled={isLoading}
              InputProps={{ endAdornment: <InputAdornment position="end"><IconButton aria-label={showConfirmation ? t('auth.hidePassword') : t('auth.showPassword')} onClick={() => setShowConfirmation(value => !value)} edge="end">{showConfirmation ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }}
            />
            
            {/* Company Information Section */}
            <Typography variant="overline" sx={{ gridColumn: '1 / -1', mt: 1.5, color: 'primary.main', fontWeight: 800, letterSpacing: '.08em' }}>
              {t('auth.companyInfo')}
            </Typography>
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="companyName"
              label={t('auth.companyName')}
              name="companyName"
              autoComplete="organization"
              value={form.companyName}
              onChange={handleChange}
              disabled={isLoading}
            />
            
            <TextField
              margin="normal"
              fullWidth
              id="companyAddress"
              label={`${t('auth.companyAddress')} (${t('common.optional')})`}
              name="companyAddress"
              autoComplete="street-address"
              value={form.companyAddress}
              onChange={handleChange}
              disabled={isLoading}
              multiline
              rows={2}
              sx={{ gridColumn: '1 / -1' }}
            />
            
            <PhoneInput
              value={form.companyPhone}
              onChange={(value) => setForm({ ...form, companyPhone: value })}
              label={`${t('auth.companyPhone')} (${t('common.optional')})`}
              disabled={isLoading}
              fullWidth
            />
            
            <TextField
              margin="normal"
              fullWidth
              id="companyEmail"
              label={`${t('auth.companyEmail')} (${t('common.optional')})`}
              name="companyEmail"
              type="email"
              autoComplete="email"
              value={form.companyEmail}
              onChange={handleChange}
              disabled={isLoading}
            />
            
            <TextField
              margin="normal"
              fullWidth
              id="companyWebsite"
              label={`${t('auth.companyWebsite')} (${t('common.optional')})`}
              name="companyWebsite"
              type="url"
              autoComplete="url"
              value={form.companyWebsite}
              onChange={handleChange}
              disabled={isLoading}
              placeholder="https://"
            />

            <TextField
              margin="normal"
              fullWidth
              id="referralCode"
              label={`${t('auth.referralCode')} (${t('common.optional')})`}
              name="referralCode"
              value={form.referralCode}
              onChange={handleChange}
              disabled={isLoading}
              inputProps={{ maxLength: 12, style: { textTransform: 'uppercase' } }}
              helperText={t('auth.referralCodeHint')}
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1', mt: 1 }}>{t('auth.registrationNextStep')}</Typography>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{ gridColumn: '1 / -1', minHeight: 48 }}
            >
              {isLoading ? <CircularProgress size={24} /> : t('auth.registerButton')}
            </Button>
            
            <Box textAlign="center" sx={{ gridColumn: '1 / -1', pt: 1 }}>
              <Typography variant="body2">
                {t('auth.alreadyHaveAccount')}{' '}
                <Typography component={Link} to="/login" sx={theme => ({ color: 'primary.main', fontWeight: 700, textDecoration: 'none', ...createTextActionSx(theme) })}>
                  {t('auth.login')}
                </Typography>
              </Typography>
            </Box>
          </Box>
    </AuthPageShell>
  );
};

export default Register;
